import {
  createExecutionEngineClient,
  type ExecutionEngineClient,
  type ExecutionEngineRequestContext,
} from "@repo/execution-engine-client";
import type { CapabilityScope } from "@repo/capability-token";
import {
  getExecutionEngineBaseUrl,
  getExecutionEngineRequestTimeoutMs,
} from "~/lib/env";
import {
  BROWSE_SCOPES,
  getAppAuthCapabilityToken,
  getAppAuthTransportAuthHeaders,
  refreshAppAuthCapabilityToken,
  type AppAuthCapabilityTokenRequest,
} from "~/lib/auth/app-auth-runtime";
import {
  toPlatformChainPreference,
  type VmPreference,
} from "~/lib/auth/vm-types";
import { getActiveSentryTraceparent } from "~/lib/sentry-traceparent";

let cachedClient: ExecutionEngineClient | null | undefined;

export interface BrowserExecutionEngineClientOptions {
  readonly vmPreference?: VmPreference;
  readonly walletAddress?: string | null;
}

export const GENERIC_OPERATION_FALLBACK_EXECUTE_SCOPES = [
  "execute:trade",
  "execute:transfer",
  "execute:withdraw",
  "execute:bridge",
  "execute:deposit",
  "execute:rebalance",
] as const satisfies readonly CapabilityScope[];

// Mirrors the unambiguous entries in STATIC_CAPABILITY_SCOPE_BY_ACTION_TYPE in
// apps/execution-engine/src/routes/generic-operation-scopes.ts. The
// derivation below deliberately excludes action types whose engine result also
// depends on args; the scanner test keeps this mirror from drifting.
export const GENERIC_OPERATION_STATIC_CAPABILITY_SCOPE_BY_ACTION_TYPE = {
  // Category aliases.
  bridge: "execute:bridge",
  "yield-withdraw": "execute:withdraw",
  "yield-redeem": "execute:withdraw",
  "yield-deposit": "execute:deposit",

  // Trade-shaped explicit actions.
  lifi_swap: "execute:trade",
  bebop_rfq_swap: "execute:trade",
  bebop_jam_swap: "execute:trade",
  ondo_direct_mint_redeem: "execute:trade",
  ondo_direct_batch_rebalance: "execute:rebalance",
  rebalance_batch: "execute:rebalance",
  zeroex_swap: "execute:trade",

  // Bridge actions use the dedicated vocabulary entry when present.
  lifi_bridge: "execute:bridge",

  // Yield actions that have an unambiguous existing capability vocabulary.
  erc4626_deposit: "execute:deposit",
  fluid_vault_deposit: "execute:deposit",
  morpho_vault_deposit: "execute:deposit",
  weth_wrap: "execute:deposit",
  erc4626_withdraw: "execute:withdraw",
  erc4626_redeem: "execute:withdraw",
  fluid_vault_withdraw: "execute:withdraw",
  fluid_vault_redeem: "execute:withdraw",
  morpho_vault_withdraw: "execute:withdraw",
  morpho_vault_redeem: "execute:withdraw",
  aave_v3_withdraw: "execute:withdraw",
  aave_v3_gateway_withdraw: "execute:withdraw",
  weth_unwrap: "execute:withdraw",

  // Concentrated-liquidity family (dual-investment accumulator): entering the
  // position is deposit-shaped, exiting/collecting is withdraw-shaped.
  cl_mint: "execute:deposit",
  cl_increase: "execute:deposit",
  cl_decrease: "execute:withdraw",
  cl_collect: "execute:withdraw",
  cl_burn: "execute:withdraw",
} as const satisfies Record<string, CapabilityScope>;

export const GENERIC_OPERATION_AMBIGUOUS_ACTION_TYPES = [
  "swap",
  "aave_v3_deposit",
  "aave_v3_gateway_deposit",
  "transfer",
  "native_transfer",
  "erc20_transfer",
] as const;

const genericOperationAmbiguousActionTypes = new Set<string>(
  GENERIC_OPERATION_AMBIGUOUS_ACTION_TYPES,
);

const genericOperationStaticCapabilityScopes = new Map(
  Object.entries(GENERIC_OPERATION_STATIC_CAPABILITY_SCOPE_BY_ACTION_TYPE),
);

// Kept in lockstep with the client-supported entries in
// EXECUTION_CAPABILITY_SCOPE_BY_ROUTE in
// apps/execution-engine/src/routes/execution-auth.ts.
export const EXECUTION_ENGINE_CAPABILITY_SCOPE_BY_ROUTE = {
  bridgeCreate: "execute:bridge",
  buyCreate: "execute:trade",
  buyPreview: "quote:read",
  depositAttachTransaction: "execute:deposit",
  depositCreate: "execute:deposit",
  genericOperationCreate: "execute:trade",
  genericOperationRead: "feed:read",
  marketOrderCreate: "execute:trade",
  marketOrderQuotePreview: "quote:read",
  ondoDirectRebalanceCreate: "execute:rebalance",
  ondoDirectRebalancePreview: "execute:rebalance",
  operationLiveDescriptor: "feed:read",
  portfolioLiveDescriptor: "feed:read",
  rebalanceCreate: "execute:rebalance",
  rebalancePreview: "execute:rebalance",
  transferCreate: "execute:transfer",
  withdrawalCreate: "execute:withdraw",
} as const satisfies Record<string, CapabilityScope>;

type ExecutionCapabilityRoute =
  keyof typeof EXECUTION_ENGINE_CAPABILITY_SCOPE_BY_ROUTE;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readJsonRecord(
  body: string | undefined,
): Record<string, unknown> | null {
  if (!body) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(body);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function readStringProperty(
  record: Record<string, unknown>,
  key: string,
): string | null {
  const value = record[key];
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function normalizePortfolioIds(
  portfolioIds: readonly string[],
): readonly string[] {
  return Array.from(
    new Set(
      portfolioIds
        .map((portfolioId) => portfolioId.trim())
        .filter((portfolioId) => portfolioId.length > 0),
    ),
  ).sort();
}

function readPortfolioIdsFromBody(
  route: ExecutionCapabilityRoute,
  body: string | undefined,
): readonly string[] {
  const parsed = readJsonRecord(body);
  if (!parsed) {
    return [];
  }

  const portfolioIds: string[] = [];
  const addPortfolioId = (value: string | null): void => {
    if (value) {
      portfolioIds.push(value);
    }
  };

  switch (route) {
    case "genericOperationCreate": {
      const actions = parsed.actions;
      if (!Array.isArray(actions)) {
        break;
      }
      for (const action of actions) {
        if (!isRecord(action)) {
          continue;
        }
        const scope = action.scope;
        if (isRecord(scope)) {
          addPortfolioId(readStringProperty(scope, "portfolioId"));
        }
        const args = action.args;
        if (isRecord(args)) {
          addPortfolioId(readStringProperty(args, "toPortfolioId"));
        }
      }
      break;
    }
    case "marketOrderCreate":
    case "marketOrderQuotePreview":
    case "buyCreate":
    case "buyPreview":
      addPortfolioId(readStringProperty(parsed, "portfolioId"));
      addPortfolioId(readStringProperty(parsed, "toPortfolioId"));
      break;
    case "transferCreate":
      addPortfolioId(readStringProperty(parsed, "fromPortfolioId"));
      addPortfolioId(readStringProperty(parsed, "toPortfolioId"));
      break;
    case "bridgeCreate":
    case "depositCreate":
    case "ondoDirectRebalanceCreate":
    case "ondoDirectRebalancePreview":
    case "rebalanceCreate":
    case "rebalancePreview":
    case "withdrawalCreate":
      addPortfolioId(readStringProperty(parsed, "portfolioId"));
      break;
    case "depositAttachTransaction":
    case "genericOperationRead":
    case "operationLiveDescriptor":
    case "portfolioLiveDescriptor":
      break;
  }

  return normalizePortfolioIds(portfolioIds);
}

function deriveGenericOperationCapabilityScopes(
  body: string | undefined,
): readonly CapabilityScope[] {
  const parsed = readJsonRecord(body);
  const actions = parsed?.actions;
  if (!Array.isArray(actions) || actions.length === 0) {
    return GENERIC_OPERATION_FALLBACK_EXECUTE_SCOPES;
  }

  const scopes = new Set<CapabilityScope>();
  for (const action of actions) {
    if (!isRecord(action) || typeof action.type !== "string") {
      return GENERIC_OPERATION_FALLBACK_EXECUTE_SCOPES;
    }

    if (genericOperationAmbiguousActionTypes.has(action.type)) {
      return GENERIC_OPERATION_FALLBACK_EXECUTE_SCOPES;
    }

    const scope = genericOperationStaticCapabilityScopes.get(action.type);
    if (!scope) {
      return GENERIC_OPERATION_FALLBACK_EXECUTE_SCOPES;
    }
    scopes.add(scope);
  }

  return [...scopes];
}

function getExecutionCapabilityRoute(
  path: string,
): ExecutionCapabilityRoute | null {
  switch (path) {
    case "/execution/bridges":
      return "bridgeCreate";
    case "/execution/buys":
      return "buyCreate";
    case "/execution/buys/preview":
      return "buyPreview";
    case "/execution/deposits":
      return "depositCreate";
    case "/execution/market-orders":
      return "marketOrderCreate";
    case "/execution/market-order-quotes":
      return "marketOrderQuotePreview";
    case "/execution/ondo-direct/rebalances":
      return "ondoDirectRebalanceCreate";
    case "/execution/ondo-direct/rebalances/preview":
      return "ondoDirectRebalancePreview";
    case "/execution/operations":
      return "genericOperationCreate";
    case "/execution/rebalances":
      return "rebalanceCreate";
    case "/execution/rebalances/preview":
      return "rebalancePreview";
    case "/execution/transfers":
      return "transferCreate";
    case "/execution/withdrawals":
      return "withdrawalCreate";
    default:
      break;
  }

  if (/^\/execution\/deposits\/[^/]+\/transaction$/u.test(path)) {
    return "depositAttachTransaction";
  }
  if (/^\/execution\/operations\/[^/]+\/live-descriptor$/u.test(path)) {
    return "operationLiveDescriptor";
  }
  if (/^\/execution\/operations\/[^/]+$/u.test(path)) {
    return "genericOperationRead";
  }
  if (/^\/execution\/portfolios\/[^/]+\/live-descriptor$/u.test(path)) {
    return "portfolioLiveDescriptor";
  }

  return null;
}

function getCapabilityRequest(
  context: ExecutionEngineRequestContext | undefined,
): AppAuthCapabilityTokenRequest {
  const route = context ? getExecutionCapabilityRoute(context.path) : null;
  if (!context || !route) {
    const portfolioIds = context?.options?.capability?.portfolioIds;
    return {
      scopes: BROWSE_SCOPES,
      ...(portfolioIds !== undefined ? { portfolioIds } : {}),
    };
  }

  const scope = EXECUTION_ENGINE_CAPABILITY_SCOPE_BY_ROUTE[route];
  if (!scope.startsWith("execute:")) {
    const portfolioIds = context.options?.capability?.portfolioIds;
    return {
      scopes: BROWSE_SCOPES,
      ...(portfolioIds !== undefined ? { portfolioIds } : {}),
    };
  }

  const scopes =
    route === "genericOperationCreate"
      ? (context.options?.capability?.scopes ??
        deriveGenericOperationCapabilityScopes(context.body))
      : [scope];
  const portfolioIds =
    context.options?.capability?.portfolioIds ??
    readPortfolioIdsFromBody(route, context.body);

  if (scopes.some((candidate) => candidate.startsWith("execute:"))) {
    if (portfolioIds.length === 0) {
      throw new Error(
        `execution capability mint requires portfolio ids: ${context.path}`,
      );
    }

    return {
      scopes,
      portfolioIds,
      ttlSeconds: 60,
    };
  }

  return {
    scopes,
    ...(context.options?.capability?.portfolioIds !== undefined
      ? { portfolioIds }
      : {}),
  };
}

function nonEmptyHeaderValue(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

function getExecutionEngineHeaders(
  options: BrowserExecutionEngineClientOptions | undefined,
) {
  return async (context?: ExecutionEngineRequestContext) => {
    const authHeaders = await getAppAuthTransportAuthHeaders();
    const capabilityToken = nonEmptyHeaderValue(
      await getAppAuthCapabilityToken(getCapabilityRequest(context)),
    );
    const vmPreference =
      options?.vmPreference && options.vmPreference !== "auto"
        ? options.vmPreference
        : undefined;
    const chainPreference = toPlatformChainPreference(vmPreference);
    const walletAddress = nonEmptyHeaderValue(options?.walletAddress);
    const traceparent = getActiveSentryTraceparent();

    return {
      ...authHeaders,
      ...(capabilityToken ? { "x-glider-capability": capabilityToken } : {}),
      ...(traceparent ? { traceparent } : {}),
      ...(vmPreference ? { "x-vm-preference": vmPreference } : {}),
      ...(chainPreference ? { "x-chain-preference": chainPreference } : {}),
      ...(walletAddress ? { "x-wallet-address": walletAddress } : {}),
    };
  };
}

function refreshExecutionEngineCapabilityToken(
  context?: ExecutionEngineRequestContext,
): Promise<string | null> {
  return refreshAppAuthCapabilityToken(getCapabilityRequest(context));
}

export function getBrowserExecutionEngineClient(
  options?: BrowserExecutionEngineClientOptions,
): ExecutionEngineClient | null {
  if (options?.vmPreference || options?.walletAddress) {
    return createExecutionEngineClient({
      baseUrl: getExecutionEngineBaseUrl(),
      headers: getExecutionEngineHeaders(options),
      onCapabilityUnauthorized: refreshExecutionEngineCapabilityToken,
      requestTimeoutMs: getExecutionEngineRequestTimeoutMs(),
    });
  }

  if (cachedClient !== undefined) {
    return cachedClient;
  }

  cachedClient = createExecutionEngineClient({
    baseUrl: getExecutionEngineBaseUrl(),
    headers: getExecutionEngineHeaders(undefined),
    onCapabilityUnauthorized: refreshExecutionEngineCapabilityToken,
    requestTimeoutMs: getExecutionEngineRequestTimeoutMs(),
  });
  return cachedClient;
}

export function resetBrowserExecutionEngineClientForTests(): void {
  cachedClient = undefined;
}
